import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { getFinancialSummary, getDailySummary } from './FinancialService';
import { FinancialSummaryResponse, DailySpendSummaryResponse } from './types';
import PeriodSelector, {
  Period,
  shiftDate,
  isFutureRef,
  weekStart,
} from './PeriodSelector';
import SpendingBarChart from './SpendingBarChart';

// ─── design tokens ────────────────────────────────────────────────────────────
const C = {
  needs: '#159b72',
  wants: '#EC4899',
  savings: '#3B82F6',
  red: '#ff454d',
  orange: '#ff8b32',
  green: '#159b72',
  blue: '#1475d1',
  muted: '#78869a',
  ink: '#17263d',
  wash: '#f4f7fb',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

// ─── BucketBar ────────────────────────────────────────────────────────────────
function BucketBar({
  label,
  color,
  actual,
  target,
  amount,
}: {
  label: string;
  color: string;
  actual: number;
  target: number;
  amount: number;
}) {
  const over = actual > target;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: C.ink }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: C.muted }}>
            {gbp(amount)}
          </Typography>
          <Chip
            label={`${actual}% / ${target}%`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: over ? alpha(C.red, 0.1) : alpha(color, 0.12),
              color: over ? C.red : color,
              fontWeight: 600,
            }}
          />
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(actual, 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: alpha(color, 0.12),
          '& .MuiLinearProgress-bar': {
            bgcolor: over ? C.red : color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
}

// ─── KpiCard ──────────────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  sub,
  loading,
  valueColor,
}: {
  label: string;
  value: string;
  sub?: string;
  loading: boolean;
  valueColor?: string;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="caption"
          sx={{
            color: C.muted,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={120} height={52} />
        ) : (
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ mt: 0.5, color: valueColor ?? C.ink }}
          >
            {value}
          </Typography>
        )}
        {sub && (
          <Typography variant="body2" sx={{ mt: 1, color: C.muted }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── MoneyPage ────────────────────────────────────────────────────────────────
const MoneyPage = (): JSX.Element => {
  const now = useMemo(() => new Date(), []);

  const [period, setPeriod] = useState<Period>('month');
  const [refDate, setRefDate] = useState<Date>(now);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const year =
    period === 'custom' && customFrom
      ? parseInt(customFrom.slice(0, 4), 10)
      : refDate.getFullYear();
  const month =
    period === 'custom' && customFrom
      ? parseInt(customFrom.slice(5, 7), 10)
      : refDate.getMonth() + 1;

  const [summary, setSummary] = useState<FinancialSummaryResponse | null>(null);
  const [daily, setDaily] = useState<DailySpendSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      getFinancialSummary(year, month),
      getDailySummary(year, month),
    ])
      .then(([sumRes, dailyRes]) => {
        setSummary(sumRes.data ?? null);
        setDaily(dailyRes.data ?? null);
      })
      .catch(() => setError('Could not load financial summary.'))
      .finally(() => setLoading(false));
  }, [year, month]);

  // Period-scoped spend / target
  const { spent, target } = useMemo(() => {
    if (!summary) return { spent: 0, target: 0 };
    const totalSpent =
      (summary.needs ?? 0) + (summary.wants ?? 0) + (summary.savings ?? 0);
    const monthTarget = daily?.monthlyTotalTarget ?? summary.income ?? 0;

    if (period === 'month') return { spent: totalSpent, target: monthTarget };

    const daysInMonth = new Date(year, month, 0).getDate();

    if (period === 'day') {
      const todayIso = new Date().toISOString().slice(0, 10);
      const dayEntry = daily?.entries?.find((e) => e.date === todayIso);
      return { spent: dayEntry?.total ?? 0, target: monthTarget / daysInMonth };
    }

    if (period === 'week') {
      const ws = weekStart(refDate);
      const we = new Date(ws);
      we.setDate(ws.getDate() + 6);
      const weekSpent = (daily?.entries ?? [])
        .filter((e) => {
          const d = new Date(e.date);
          return d >= ws && d <= we;
        })
        .reduce((s, e) => s + e.total, 0);
      return { spent: weekSpent, target: (monthTarget / daysInMonth) * 7 };
    }

    if (period === 'custom' && customFrom && customTo) {
      const rangeSpent = (daily?.entries ?? [])
        .filter((e) => e.date >= customFrom && e.date <= customTo)
        .reduce((s, e) => s + e.total, 0);
      const daysInRange = Math.max(
        Math.round(
          (new Date(customTo + 'T00:00:00').getTime() -
            new Date(customFrom + 'T00:00:00').getTime()) /
            86_400_000,
        ) + 1,
        1,
      );
      return {
        spent: rangeSpent,
        target: (monthTarget / daysInMonth) * daysInRange,
      };
    }

    return { spent: totalSpent, target: monthTarget };
  }, [summary, daily, period, refDate, year, month, customFrom, customTo]);

  const netPosition = summary?.netPosition ?? 0;
  const needsPct = summary ? pct(summary.needs, summary.income) : 0;
  const wantsPct = summary ? pct(summary.wants, summary.income) : 0;
  const savingsPct = summary ? pct(summary.savings, summary.income) : 0;

  const handlePrev = () => setRefDate((d) => shiftDate(period, d, -1));
  const handleNext = () => {
    const next = shiftDate(period, refDate, 1);
    if (!isFutureRef(period, next, now)) setRefDate(next);
  };
  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    if (p !== 'custom') setRefDate(new Date(now));
  };
  const handleCustomApply = (from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
  };

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
              Money
            </Typography>
            <Typography variant="body2" sx={{ color: C.muted }}>
              Income, spending and ratios
            </Typography>
          </Box>

          {/* Period selector */}
          <PeriodSelector
            period={period}
            refDate={refDate}
            now={now}
            onPeriodChange={handlePeriodChange}
            onPrev={handlePrev}
            onNext={handleNext}
            customFrom={customFrom}
            customTo={customTo}
            onCustomApply={handleCustomApply}
          />

          {error && (
            <Card sx={{ mb: 3, border: `1px solid ${alpha(C.red, 0.3)}` }}>
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={3}>
            {/* Net Position */}
            <Grid item xs={12} md={4}>
              <KpiCard
                label="Net Position"
                value={summary ? gbp(netPosition) : '—'}
                sub="Income minus spend this month"
                loading={loading}
                valueColor={netPosition >= 0 ? C.green : C.red}
              />
            </Grid>

            {/* Income */}
            <Grid item xs={12} md={4}>
              <KpiCard
                label="Income"
                value={summary ? gbp(summary.income) : '—'}
                sub={
                  summary
                    ? `${summary.transactionCount} transactions`
                    : undefined
                }
                loading={loading}
              />
            </Grid>

            {/* Total Spend */}
            <Grid item xs={12} md={4}>
              <KpiCard
                label="Total Spend"
                value={summary ? gbp(spent) : '—'}
                sub={
                  target > 0
                    ? `of ${gbp(target)} target`
                    : 'Needs + Wants + Savings'
                }
                loading={loading}
                valueColor={target > 0 && spent > target ? C.red : undefined}
              />
            </Grid>

            {/* Ratio Breakdown */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    gutterBottom
                    sx={{ color: C.ink }}
                  >
                    Ratio Breakdown
                  </Typography>
                  <Typography variant="body2" sx={{ color: C.muted, mb: 2.5 }}>
                    How this month's spend compares to your target ratios
                  </Typography>
                  {loading ? (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Skeleton
                        variant="rectangular"
                        height={40}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={40}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={40}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                      }}
                    >
                      <BucketBar
                        label="Needs"
                        color={C.needs}
                        actual={needsPct}
                        target={summary?.needsTarget ?? 50}
                        amount={summary?.needs ?? 0}
                      />
                      <BucketBar
                        label="Wants"
                        color={C.wants}
                        actual={wantsPct}
                        target={summary?.wantsTarget ?? 30}
                        amount={summary?.wants ?? 0}
                      />
                      <BucketBar
                        label="Savings"
                        color={C.savings}
                        actual={savingsPct}
                        target={summary?.savingsTarget ?? 20}
                        amount={summary?.savings ?? 0}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Spending vs Budget chart */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    gutterBottom
                    sx={{ color: C.ink }}
                  >
                    Spending vs Budget
                  </Typography>
                  <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>
                    Daily spend against your target budget
                  </Typography>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={140}
                      sx={{ borderRadius: 2 }}
                    />
                  ) : (daily?.entries?.length ?? 0) > 0 ? (
                    <SpendingBarChart
                      entries={daily!.entries ?? []}
                      period={period}
                      refDate={refDate}
                      monthlyTarget={daily!.monthlyTotalTarget}
                      customFrom={customFrom}
                      customTo={customTo}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: C.muted }}>
                        No transaction data for this period
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {[
                  {
                    label: 'Transactions',
                    href: '/user/transactions',
                    desc: 'View and filter all transactions',
                  },
                  {
                    label: 'Spending Analysis',
                    href: '/user/spending-analysis',
                    desc: 'Needs, Wants, Savings breakdown',
                  },
                  {
                    label: 'Commitments',
                    href: '/user/commitments',
                    desc: 'Regular bills and subscriptions',
                  },
                  {
                    label: 'Goals',
                    href: '/user/goals',
                    desc: 'Savings targets and progress',
                  },
                ].map((link) => (
                  <Grid item xs={12} sm={4} key={link.label}>
                    <Card
                      component="a"
                      href={link.href}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        transition: 'box-shadow 0.2s',
                        '&:hover': {
                          boxShadow: '0 16px 40px rgba(22,54,93,0.12)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{ color: C.ink }}
                        >
                          {link.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.muted }}>
                          {link.desc}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </UserDashboard>
  );
};

export default MoneyPage;
